import { useState } from "react";
import { compressToUTF16, decompressFromUTF16 } from "lz-string";
import { supabase } from "./supabaseClient";
import { User } from "@supabase/supabase-js";

export type FlowMetadata = {
  id: string;
  name: string;
  path: string;
  created_at: string;
  updated_at: string;
};

export type FlowStorageObjMetadata = {
  d: string; // The flow as a string
  hash: string; // The SHA-256 hash of the flow
  ts: number; // Timestamp of when the flow was last updated
};

async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function decompressFlowStorageObj(
  compressed_text: string,
): FlowStorageObjMetadata {
  return JSON.parse(decompressFromUTF16(compressed_text));
}

export function useFlowStorage() {
  const [loading, setLoading] = useState(false);
  const [checkingConsistency, setCheckingConsistency] = useState(false);

  // After uploading a file to Supabase Storage, it may take some time for the file to be available.
  // Continued checking if the file is updated and available in Supabase Storage
  const checkConsistency = async (filePath: string, hash: string) => {
    setCheckingConsistency(true);

    // Poll for file availability
    const checkInterval = setInterval(async () => {
      try {
        const { data, error } = await supabase.storage
          .from("flows")
          .createSignedUrl(filePath, 60); // 60 seconds validity

        if (data && !error) {
          // Try to fetch the file to confirm it's available
          // :: Note: We ignore the error here, as it may be due to the file not being available yet
          const res = await fetch(`${data.signedUrl}&cb=${Date.now()}`);
          if (res?.ok) {
            // Check if the file we retrieved has the same hash as the one we uploaded
            const compressed_flow_storage_obj = await res.text();
            const flow_storage_obj: FlowStorageObjMetadata =
              decompressFlowStorageObj(compressed_flow_storage_obj);
            if (flow_storage_obj.hash === hash) {
              // We're done!
              console.log("Updated flow is now available in the cloud.");
              clearInterval(checkInterval);
              setCheckingConsistency(false);
            }
          }
        }
      } catch (err) {
        console.log("Still checking consistency...", err);
      }
    }, 1000); // Check every second

    // Set a timeout to eventually give up checking
    setTimeout(() => {
      clearInterval(checkInterval);
      setCheckingConsistency(false);
      // File might still be propagating
    }, 20000); // Give up after 20 seconds
  };

  const renameFlow = async (
    flowId: string,
    newName: string,
    user: User | null,
  ) => {
    if (!user) throw new Error("Not logged in.");
    if (!flowId) throw new Error("Flow ID is required.");
    if (!newName) throw new Error("New name is required.");
    const userId = user.id;
    // Update the flow name in the database
    const { error } = await supabase.from("flows").upsert(
      {
        id: flowId,
        user_id: userId,
        name: newName,
      },
      {
        onConflict: "id", // Ensure we only update the flow's name with the given id
      },
    );
    if (error) throw error;
  };

  const uploadFlow = async (
    flow: object,
    name: string,
    id: string | null,
    user: User | null,
  ): Promise<string | undefined> => {
    if (loading)
      throw new Error(
        "Already uploading a flow. Please wait for the current operation to complete.",
      );
    if (!flow) throw new Error("Flow is required.");

    setLoading(true);

    if (!user) throw new Error("Not logged in");
    const userId = user.id;

    // Convert flow to JSON string
    const flowJson = JSON.stringify(flow);

    // Calculate SHA-256 hash of the flow
    const hash = await sha256(flowJson);

    // Wrap the flow in metadata on when it was last updated and its hash
    const flowMeta: FlowStorageObjMetadata = {
      d: flowJson, // we store the flow as a string here so we don't have to waste resources re-stringifying it
      hash: hash,
      ts: Date.now(),
    };

    // Compress the flow
    const compressed = compressToUTF16(JSON.stringify(flowMeta));
    const blob = new Blob([compressed], { type: "text/plain" });

    // Check file size (25MB max)
    const maxSizeMB = 25;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (blob.size > maxSizeBytes) {
      setLoading(false);
      throw new Error(
        `Flow size exceeds the maximum limit of ${maxSizeMB}MB after compression. Either delete parts of the flow, or export your flow to a file and upload it manually.`,
      );
    }

    // Save metadata to Supabase Database, and get the flow id
    // Update existing flow if id is provided
    if (id != null) {
      // We need to use update here to avoid the INSERT policy trigger
      const { error: updateError } = await supabase
        .from("flows")
        .update({
          name,
          updated_at: new Date(),
        })
        .eq("id", id)
        .eq("user_id", user.id); // For extra safety

      if (updateError) {
        setLoading(false);
        throw updateError;
      }
    } else {
      // Insert a new flow into the database
      const { data, error: dbError } = await supabase
        .from("flows")
        .upsert(
          {
            id: id || undefined, // Pass id only if it's not null
            user_id: userId,
            name,
          },
          {
            onConflict: "id", // Ensure we only update the flow's name with the given id, if id already exists
          },
        )
        .select();

      // Store the new flow id
      id = data?.[0]?.id || id;

      if (dbError) {
        setLoading(false);
        throw dbError;
      } else if (id == null) {
        throw new Error("Unexpected error: Flow ID is null.");
      }
    }

    const filePath = `flows/${userId}/${id}.cforge`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from("flows")
      .upload(filePath, blob, { upsert: true, cacheControl: "3600" });

    setLoading(false);

    if (uploadError) throw uploadError;

    // Keeping checking if the new file version is available,
    // as changes may take some time to propagate
    // :: Note: This handles setting 'loading' to false once it's available.
    await checkConsistency(filePath, hash);

    // Return the id of the row (either existing or newly created)
    return id;
  };

  const downloadFlow = async (flowId: string, user: User | null) => {
    if (loading)
      throw new Error(
        "Already uploading/downloading a flow. Please wait for the current operation to complete.",
      );
    if (!user) throw new Error("Not logged in.");
    if (!flowId) throw new Error("Flow ID is required.");

    // Calculate the file path
    const filePath = `flows/${user.id}/${flowId}.cforge`;

    setLoading(true);

    // Download the flow from Supabase Storage
    // NOTE: We use createSignedUrl, so that we can bypass cache.
    const { data: metaData, error } = await supabase.storage
      .from("flows")
      .createSignedUrl(filePath, 60); // 60 seconds validity
    if (error) throw error;

    // Try to fetch the file to confirm it's available
    const res = await fetch(`${metaData.signedUrl}&cb=${Date.now()}`);

    setLoading(false);

    if (!res.ok) {
      throw new Error("Failed to download the flow. Please try again.");
    }

    // Decompress and extract the flow
    const compressed_flow_storage_obj = await res.text();
    const flow_storage_obj: FlowStorageObjMetadata = decompressFlowStorageObj(
      compressed_flow_storage_obj,
    );
    const flow = JSON.parse(flow_storage_obj.d); // Parse the flow

    return flow;
  };

  const deleteFlow = async (
    flowId: string,
    user: User | null,
  ): Promise<boolean> => {
    if (!user) throw new Error("Not logged in.");
    if (!flowId) throw new Error("Flow ID is required.");

    // Ask for confirmation before deleting, since this is a destructive action
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this flow? This action cannot be undone.",
    );
    if (!confirmDelete) return false;

    const { error } = await supabase
      .from("flows")
      .delete()
      .eq("id", flowId)
      .eq("user_id", user.id); // For extra safety

    if (error) throw error;

    // We also need to delete the file from Supabase Storage
    const filePath = `flows/${user.id}/${flowId}.cforge`;
    const { error: storageError } = await supabase.storage
      .from("flows")
      .remove([filePath]);
    if (storageError) {
      console.error("Error deleting file from storage:", storageError);
      throw storageError;
    }

    // Success
    console.log("Flow deleted successfully.");
    return true;
  };

  const listFlows = async (user: User | null): Promise<FlowMetadata[]> => {
    if (!user) return [];
    const userId = user.id;
    setLoading(true);

    // List all flows for the user
    const { data, error } = await supabase
      .from("flows")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false });

    setLoading(false);

    if (error) throw error;
    return data as FlowMetadata[];
  };

  return {
    renameFlow,
    uploadFlow,
    downloadFlow,
    deleteFlow,
    listFlows,
    loading,
    checkingConsistency,
  };
}
